import * as Sentry from "@sentry/nextjs";
import { Fragment } from "react";

import { renderField } from "forms/helpers/renderField";
import { questionNames } from "types/QuestionNames";
import type { FormControlType } from "types/formType";
import type { IJsonSchema, IProperty } from "types/jsonSchema";
import type { IFormInputs } from "types/uiSchema";
import { logger } from "utils/helpers/logger";

export default function useRenderFields() {
    const extractJsonData = (field: IFormInputs, jsonSchema: IJsonSchema) => {
        const jsonData: Record<string, IProperty> = {};
        const referenceKey = "x-json-schema-reference-property";

        if (field?.[referenceKey]) {
            const referenceProperty = field[referenceKey];

            if (referenceProperty && jsonSchema.properties?.[referenceProperty]) {
                jsonData[referenceProperty] = jsonSchema.properties[referenceProperty] as IProperty;
            }
        }

        if (field?.elements) {
            field.elements.forEach((element) => {
                const referenceProperty = element[referenceKey];
                if (referenceProperty && jsonSchema.properties?.[referenceProperty]) {
                    jsonData[referenceProperty] = jsonSchema.properties[referenceProperty] as IProperty;
                }
            });
        }
        return jsonData;
    };

    const renderFields = ({
        fields,
        control,
        jsonSchema,
        defaults = {},
    }: {
        fields: IFormInputs[];
        control: FormControlType;
        jsonSchema: IJsonSchema;
        defaults?: Record<string, unknown>;
    }) => {
        try {
            return fields.map((field, index) => {
                if (!field) return null;

                const name = field["x-control-name"];
                const allOf = [];

                const jsonData = extractJsonData(field, jsonSchema);

                if (name === questionNames.ShortContactData || name === questionNames.ContactData) {
                    allOf.push(...jsonSchema.allOf);
                }

                return (
                    // biome-ignore lint:
                    <Fragment key={index}>
                        {renderField({
                            field,
                            control,
                            jsonData,
                            allOf,
                            defaultValue: defaults[name],
                        })}
                    </Fragment>
                );
            });
        } catch (e) {
            logger.error(e);
            Sentry.captureException(e);
            return null;
        }
    };

    return { renderFields };
}
